import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from '@/lib/utils'

/**
 * App-wide tooltip provider — mount once near the root. Sets the shared open
 * delay so a hint appears only on a deliberate hover, and lets a quick move
 * between neighboring triggers show instantly (no per-item wait).
 */
export function TooltipProvider({
  delayDuration = 400,
  skipDelayDuration = 200,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider delayDuration={delayDuration} skipDelayDuration={skipDelayDuration} {...props} />
}

/** Low-level primitives, for the rare tooltip that needs custom control. Most
 * call sites should use the <Tooltip> wrapper below. */
export const TooltipRoot = TooltipPrimitive.Root
export const TooltipTrigger = TooltipPrimitive.Trigger

/**
 * The floating hint itself: a small dark charcoal label with light text —
 * Notion-style. A quiet floating layer (tiny radius, the overlay shadow, a
 * short ease-out fade), portaled above dialogs. Not for rich content; keep it
 * to a few words.
 */
export const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(function TooltipContent({ className, sideOffset = 6, ...props }, ref) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          // Inverted ink, so a hint reads as a label rather than a card. A few
          // words; never the only place information lives.
          'z-[60] min-h-[26px] max-w-[240px] select-none rounded-sm bg-tooltip px-2 py-[5px] text-xs font-medium leading-4 text-tooltip-foreground shadow-md',
          'motion-safe:animate-[ds-tooltip-in_120ms_var(--ds-ease-out)]',
          className,
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  )
})

export interface TooltipProps {
  /** The short hint shown on hover / focus. A few words — falsy hides the tooltip entirely. */
  content: React.ReactNode
  /** The element the tooltip describes (must forward props/ref, e.g. a <button> or our <Button>). */
  children: React.ReactElement
  side?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>['side']
  align?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>['align']
  /** Gap between the trigger and the hint. */
  sideOffset?: number
}

/**
 * The standard tooltip: wrap any control to give it a Notion-style hint. This is
 * the sanctioned replacement for the native `title` attribute (see the
 * `cse/no-native-title` lint rule), so hints are consistently styled, themed,
 * and accessible. Falsy `content` renders the child untouched.
 *
 * @example
 * <Tooltip content="Delete the selection (Del)">
 *   <button onClick={remove}><Trash2 /></button>
 * </Tooltip>
 */
export function Tooltip({ content, children, side = 'top', align = 'center', sideOffset }: TooltipProps) {
  if (content === null || content === undefined || content === '') return children
  return (
    <TooltipRoot>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} align={align} {...(sideOffset !== undefined ? { sideOffset } : {})}>
        {content}
      </TooltipContent>
    </TooltipRoot>
  )
}
